import { Pencil1Icon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IAdminFirmware } from "../../../@types/admin/AdminFirmware";
import { TableSorted } from "../../../components/TableSorted";
import { useAdminFirmware } from "../../../hooks/queries/admin/devices";
import { useModal } from "../../../hooks/useModal";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  RowAction,
  RowActionGroup,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import {
  BinaryLink,
  GenerationTag,
  GenerationTags,
} from "./AdminFirmware.styles";
import { FirmwareModal } from "./FirmwareModal";

const SEARCH_FIELDS: (keyof IAdminFirmware)[] = [
  "device",
  "version",
  "changeLog",
  "platform",
  "binary",
];

/**
 * Firmware admin page (EN-5166), migrated from the Retool Firmware app.
 *
 * ⚠️ What is published here is what devices download. The version is
 * validated server-side and the generations list decides which hardware is
 * offered an update — an empty list offers it to nothing.
 *
 * Kept a separate page from Cellular Devices: `page_CellularDevices` carried
 * copies of this page's firmware queries, but they referenced widgets that
 * did not exist there. See retool-migration.md for the scope decision.
 */
export function AdminFirmware() {
  document.title = "Firmware - Admin";

  const { openModal, closeModal } = useModal();
  const { firmware, isLoading, isError } = useAdminFirmware();

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: firmware,
    searchFields: SEARCH_FIELDS,
  });

  const openEditor = (record?: IAdminFirmware) =>
    openModal(<FirmwareModal firmware={record} onClose={closeModal} />, {
      width: "min(560px, 94vw)",
      maxHeight: "90vh",
      showCloseButton: true,
    });

  const columns: ColumnDef<IAdminFirmware, string>[] = useMemo(
    () => [
      {
        id: "id",
        header: "ID",
        accessorFn: (record) => (record.id == null ? "" : String(record.id)),
        enableSorting: true,
      },
      { accessorKey: "device", header: "Device", enableSorting: true },
      { accessorKey: "version", header: "Version", enableSorting: true },
      {
        // Booleans go through an accessorFn returning a display string:
        // TableSorted renders "-" and skips the cell renderer for a falsy
        // value, which would hide every unpublished row's badge.
        id: "publish",
        header: "Published",
        accessorFn: (record) => (record.publish ? "Yes" : "No"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.publish ? "active" : "inactive"}>
            {row.original.publish ? "Yes" : "No"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "generations",
        header: "Generations",
        accessorFn: (record) => (record.generations ?? []).join(", "),
        cell: ({ row }) => (
          <GenerationTags>
            {(row.original.generations ?? []).map((generation) => (
              <GenerationTag key={generation}>{generation}</GenerationTag>
            ))}
          </GenerationTags>
        ),
        enableSorting: false,
      },
      { accessorKey: "platform", header: "Platform", enableSorting: true },
      {
        id: "binary",
        header: "Binary",
        accessorFn: (record) => record.binary ?? "",
        cell: ({ row }) =>
          // Retool built this link by concatenating AWS_S3_URL + "/media/" +
          // binary, which cannot work for a private device. `url` is the
          // API's, pre-signed where it needs to be.
          row.original.url ? (
            <BinaryLink
              href={row.original.url}
              target="_blank"
              rel="noreferrer"
            >
              {row.original.binary}
            </BinaryLink>
          ) : (
            <span>{row.original.binary}</span>
          ),
        enableSorting: true,
      },
      { accessorKey: "changeLog", header: "Changelog", enableSorting: true },
      {
        accessorKey: "created",
        header: "Created",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.created, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActionGroup>
            <RowAction
              label="Edit"
              icon={<Pencil1Icon />}
              onClick={() => {
                openEditor(row.original);
              }}
            />
          </RowActionGroup>
        ),
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>Firmware</PageTitle>
          <PageInfoTooltip title="Firmware">
            Firmware binaries offered to patient devices. Publishing a record
            makes it available for download by the generations it lists.
          </PageInfoTooltip>
        </PageTitleGroup>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by device, version, platform or changelog..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FiltersActions>
          <FilterButton variant="primary" onClick={() => openEditor()}>
            Add firmware
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the firmware list. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-firmware"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "firmware",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
